import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Sun, Moon, Pencil, Check, X, Settings } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { TYPE } from '../../constants/typeScale';

interface Props {
  name: string;
  email: string;
  /** The user's profile image (generated/uploaded or social) — shown instead of the initial. */
  avatar?: string;
  alias?: string;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onSignOut: () => void;
  /** Persist a new display alias ('' clears it). */
  onSaveAlias: (alias: string) => void | Promise<void>;
}

const menuRow =
  `flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left ${TYPE.body} text-secondary-600 dark:text-secondary-300 hover:bg-secondary-100 hover:text-secondary-900 dark:hover:bg-secondary-800 dark:hover:text-white transition-colors`;

const iconBtn =
  'p-1.5 rounded-lg text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors';

/**
 * The account menu — identity, and only the controls a creator reaches for constantly:
 *   row 1: avatar + display name (full width, never truncated) + edit-alias affordance
 *   then labelled rows: Settings · Sign out
 *
 * LABELLED ROWS (navigation review, owner 2026-09-26). This used to end in two bare icons, one of which signed
 * you out; and Settings was reachable twice (a gear on the rail and the avatar here as "Your profile"). Now
 * the menu is the one door to Settings, and each action says what it does in words.
 * The user can set a display alias (overrides their account name across the app).
 *
 * RF3-QUIET (owner 2026-08-23) emptied this rail of everything else: the credit + storage meters now
 * live only in Settings -> Usage, and the help/legal links plus the admin cost page moved into
 * Settings. A 272px rail carrying two meters and seven icons made a creator account for their spend
 * every time they looked at their own name.
 */
const SidebarFooter: React.FC<Props> = ({ name, email, avatar, alias, theme, onToggleTheme, onSignOut, onSaveAlias }) => {
  const display = (alias || name || email || 'Account').trim();
  const initial = display.charAt(0).toUpperCase();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const open = () => { setDraft(alias || name || ''); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try { await onSaveAlias(draft); setEditing(false); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-2.5 py-1">
      {/* Row 1 — identity */}
      {editing ? (
        <div className="flex items-center gap-1.5">
          <input
            autoFocus
            value={draft}
            maxLength={40}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
            placeholder="Display name"
            className={`flex-1 min-w-0 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-2.5 py-1.5 ${TYPE.body} text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500`}
          />
          <button onClick={save} disabled={saving} className={iconBtn} aria-label="Save name" title="Save name"><Check className="w-4 h-4" /></button>
          <button onClick={() => setEditing(false)} disabled={saving} className={iconBtn} aria-label="Cancel" title="Cancel"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className={`w-8 h-8 shrink-0 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center overflow-hidden ${TYPE.control} font-semibold text-secondary-600 dark:text-secondary-300`}
          >
            {avatar
              ? <img src={avatar} alt={display} referrerPolicy="no-referrer" className="w-8 h-8 rounded-full object-cover" />
              : initial}
          </span>
          <div className="min-w-0 flex-1">
            <p className={`${TYPE.body} font-medium text-secondary-800 dark:text-secondary-100 leading-tight break-words`}>{display}</p>
            {alias && <p className={`${TYPE.meta} text-secondary-400 truncate`}>{email}</p>}
          </div>
          <Tooltip content="Set a display name">
            <button onClick={open} className={iconBtn} aria-label="Set display name">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>
      )}

      {/* The actions, in words. */}
      <div className="flex flex-col border-t border-secondary-200 pt-1.5 dark:border-secondary-700">
        <Link to="/account-settings" className={menuRow}>
          <Settings className="w-4 h-4" aria-hidden />Settings
        </Link>
        {/* THE THEME TOGGLE IS HIDDEN (owner 2026-08-29): dark is the product's identity, not a preference —
            colour work is judged against a dark surround, which is why every tool a creator brings muscle
            memory from is dark. The control and its wiring are kept, not deleted, so restoring it is one flag
            (`FORCE_DARK` in ThemeContext) rather than a rebuild. */}
        <button onClick={onToggleTheme} className={`hidden ${menuRow}`} aria-label="Toggle theme">
          {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}Theme
        </button>
        <button onClick={onSignOut} className={menuRow}>
          <LogOut className="w-4 h-4" aria-hidden />Sign out
        </button>
      </div>
    </div>
  );
};

export default SidebarFooter;
